import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Dialog, DialogContent, DialogActions, Box, Typography, Button,
  IconButton, Stack, Slider, CircularProgress, Alert,
} from "@mui/material";
import Cropper, { Area } from "react-easy-crop";
import CloseIcon from "@mui/icons-material/Close";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import CachedOutlinedIcon from "@mui/icons-material/CachedOutlined";
import CenterFocusStrongOutlinedIcon from "@mui/icons-material/CenterFocusStrongOutlined";

/* ──────────────────────────────────────────────────────────────────────
   AvatarPhotoEditor — modal photo editor used for the user profile
   image flow. Two acquisition paths:

     1. Upload from device  → standard file input.
     2. Take photo (webcam) → MediaDevices.getUserMedia + canvas snapshot.

   Once an image is acquired, the user is dropped into a circular
   cropper (react-easy-crop) where they can pan + zoom to frame the
   avatar. On confirm we render the visible crop to a square canvas
   and emit a fresh Blob — the caller decides what to do with it
   (e.g. send through the existing /utility/upload endpoint).
   ────────────────────────────────────────────────────────────────────── */

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called with the final cropped image as a Blob (image/jpeg). */
  onConfirm: (blob: Blob) => void;
  /** Output edge length in pixels — square crop. Defaults to 512. */
  outputSize?: number;
}

type Stage = "choose" | "camera" | "crop";

const AvatarPhotoEditor: React.FC<Props> = ({ open, onClose, onConfirm, outputSize = 512 }) => {
  const [stage, setStage]         = useState<Stage>("choose");
  const [imageSrc, setImageSrc]   = useState<string | null>(null);
  const [crop, setCrop]           = useState({ x: 0, y: 0 });
  const [zoom, setZoom]           = useState(1);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  const [error, setError]         = useState("");
  const [processing, setProcessing] = useState(false);

  const fileInputRef    = useRef<HTMLInputElement>(null);
  const videoRef        = useRef<HTMLVideoElement>(null);
  const mediaStreamRef  = useRef<MediaStream | null>(null);

  /* ── Reset internal state every time the dialog opens. ──────────── */
  useEffect(() => {
    if (open) {
      setStage("choose");
      setImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedArea(null);
      setError("");
      setProcessing(false);
    } else {
      stopCamera();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* ── Camera lifecycle ───────────────────────────────────────────── */
  const startCamera = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      mediaStreamRef.current = stream;
      setStage("camera");
      // Attach in the next paint so the <video> exists in the DOM.
      requestAnimationFrame(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      });
    } catch (e: any) {
      const reason =
        e?.name === "NotAllowedError"  ? "Camera permission denied. Allow access in browser settings."
        : e?.name === "NotFoundError"   ? "No camera detected on this device."
        : e?.name === "NotReadableError" ? "Camera is in use by another application."
                                          : "Unable to access camera.";
      setError(reason);
    }
  };

  const stopCamera = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  /* ── Capture a still frame from the live camera → enter crop.
       The video element is mirrored on screen (scaleX(-1)) so the
       selfie reads naturally; the bare drawImage() captures the
       unmirrored feed. We re-mirror the canvas before drawing so
       what the user *saves* matches what they *saw*. We also paint
       a white base layer first so any sub-pixel gaps become white,
       never black — JPEG has no alpha. */
  const captureFromCamera = () => {
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;
    const canvas = document.createElement("canvas");
    canvas.width  = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    // 1. White base — protects against any transparent / unrasterized
    //    pixels showing up as black in the JPEG.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    // 2. Mirror horizontally to match the on-screen preview.
    ctx.save();
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, w, h);
    ctx.restore();
    const dataUrl = canvas.toDataURL("image/jpeg", 0.94);
    stopCamera();
    setImageSrc(dataUrl);
    setStage("crop");
  };

  /* ── File picker handler → enter crop. ──────────────────────────── */
  const handleFile = (file: File) => {
    setError("");
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("Image must be under 10 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        setImageSrc(reader.result);
        setStage("crop");
      }
    };
    reader.onerror = () => setError("Could not read the file.");
    reader.readAsDataURL(file);
  };

  /* ── react-easy-crop callbacks ──────────────────────────────────── */
  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setCroppedArea(areaPixels);
  }, []);

  /* ── Render the visible crop to a square canvas + emit a Blob. ── */
  const handleConfirm = async () => {
    if (!imageSrc || !croppedArea) return;
    setProcessing(true);
    setError("");
    try {
      const blob = await renderCroppedBlob(imageSrc, croppedArea, outputSize);
      onConfirm(blob);
      onClose();
    } catch {
      setError("Could not process the image. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  /* ── Render ─────────────────────────────────────────────────────── */
  return (
    <Dialog
      open={open}
      onClose={() => { stopCamera(); onClose(); }}
      maxWidth={false}
      slotProps={{
        backdrop: { sx: { bgcolor: "rgba(13,27,42,0.55)", backdropFilter: "blur(4px)" } },
      }}
      PaperProps={{
        sx: {
          borderRadius: "18px",
          width: 520,
          maxWidth: "92vw",
          overflow: "hidden",
          boxShadow: "0 24px 60px rgba(13,27,42,0.28), 0 8px 24px rgba(13,27,42,0.18)",
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        position: "relative",
        background: `
          radial-gradient(ellipse at 95% 0%, rgba(201,168,76,0.12) 0%, transparent 55%),
          linear-gradient(135deg, #1E4D8F 0%, #142b46 65%, #1c3a5f 100%)
        `,
        color: "#fff",
        px: 3.2, py: 2.2,
        borderBottom: "1px solid rgba(201,168,76,0.18)",
      }}>
        <Stack direction="row" alignItems="center" gap={1.4}>
          <Box sx={{
            width: 36, height: 36, borderRadius: "10px",
            bgcolor: "rgba(201,168,76,0.16)", color: "#D4AF37",
            display: "flex", alignItems: "center", justifyContent: "center",
            border: "1px solid rgba(201,168,76,0.30)",
          }}>
            <CameraAltOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box flex={1}>
            <Typography sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 1.3, color: "#D4AF37", textTransform: "uppercase", opacity: 0.9 }}>
              Profile Photo
            </Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.2, letterSpacing: "-0.2px", mt: 0.2 }}>
              {stage === "choose" ? "Choose Source" : stage === "camera" ? "Take a Photo" : "Frame Your Avatar"}
            </Typography>
          </Box>
          <IconButton
            onClick={() => { stopCamera(); onClose(); }}
            sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" } }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ p: "0 !important", bgcolor: "#fafbfc" }}>
        {error && (
          <Alert severity="error" sx={{ borderRadius: 0, fontSize: 12 }}>
            {error}
          </Alert>
        )}

        {/* ── STAGE 1 · CHOOSE SOURCE ─────────────────────────────── */}
        {stage === "choose" && (
          <Box sx={{ p: 3 }}>
            <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
              <SourceTile
                icon={<CameraAltOutlinedIcon sx={{ fontSize: 28 }} />}
                title="Take Photo"
                subtitle="Use your webcam to capture a new photo"
                onClick={startCamera}
              />
              <SourceTile
                icon={<PhotoLibraryOutlinedIcon sx={{ fontSize: 28 }} />}
                title="Upload File"
                subtitle="Select an image from your device"
                onClick={() => fileInputRef.current?.click()}
              />
            </Stack>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
            />
          </Box>
        )}

        {/* ── STAGE 2 · CAMERA LIVE PREVIEW ───────────────────────── */}
        {stage === "camera" && (
          <Box sx={{ p: 0 }}>
            <Box sx={{
              position: "relative", aspectRatio: "4/3", bgcolor: "#000",
              display: "flex", alignItems: "center", justifyContent: "center",
              overflow: "hidden",
            }}>
              <Box
                component="video"
                ref={videoRef}
                playsInline muted autoPlay
                sx={{
                  width: "100%", height: "100%", objectFit: "cover",
                  transform: "scaleX(-1)", // mirror — feels natural for selfie cams
                }}
              />
              {/* Soft framing circle overlay so the user knows the
                  crop will be circular even at capture time. */}
              <Box sx={{
                position: "absolute", inset: 0,
                pointerEvents: "none",
                boxShadow: "inset 0 0 0 9999px rgba(13,27,42,0.40)",
                borderRadius: "50%",
                clipPath: "circle(40% at 50% 50%)",
                WebkitClipPath: "circle(40% at 50% 50%)",
              }} />
            </Box>
          </Box>
        )}

        {/* ── STAGE 3 · CROP ───────────────────────────────────────── */}
        {stage === "crop" && imageSrc && (
          <Box>
            <Box sx={{ position: "relative", aspectRatio: "1/1", bgcolor: "#1E4D8F" }}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </Box>
            <Box sx={{ px: 3, py: 1.8, borderTop: "1px solid #eef0f4", bgcolor: "#fff" }}>
              <Stack direction="row" alignItems="center" gap={2}>
                <CenterFocusStrongOutlinedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#64748b", minWidth: 36 }}>
                  Zoom
                </Typography>
                <Slider
                  size="small"
                  value={zoom}
                  min={1} max={3} step={0.01}
                  onChange={(_, v) => setZoom(typeof v === "number" ? v : 1)}
                  sx={{
                    color: "#1a3353",
                    "& .MuiSlider-thumb": { width: 14, height: 14 },
                    "& .MuiSlider-rail":  { bgcolor: "#eef0f4", opacity: 1 },
                  }}
                />
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#1a3353", minWidth: 30, fontVariantNumeric: "tabular-nums" }}>
                  {Math.round(zoom * 100)}%
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.4 }}>
                Drag to reposition · scroll/pinch to zoom
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{
        px: 3, py: 1.8,
        bgcolor: "#fafbfc",
        borderTop: "1px solid #eef0f4",
        gap: 0.8,
      }}>
        {stage === "choose" && (
          <Button
            onClick={() => { stopCamera(); onClose(); }}
            sx={{ textTransform: "none", fontWeight: 600, fontSize: 13, color: "#64748b", borderRadius: "10px", px: 2.4 }}
          >
            Cancel
          </Button>
        )}

        {stage === "camera" && (
          <>
            <Button
              onClick={() => { stopCamera(); setStage("choose"); }}
              sx={{ textTransform: "none", fontWeight: 600, fontSize: 13, color: "#64748b", borderRadius: "10px", px: 2 }}
            >
              Back
            </Button>
            <Box flex={1} />
            <Button
              variant="contained" disableElevation
              startIcon={<CameraAltOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={captureFromCamera}
              sx={{
                textTransform: "none", fontWeight: 700, fontSize: 13,
                bgcolor: "#1E4D8F", color: "#fff",
                borderRadius: "10px", px: 2.6, py: 1,
                boxShadow: "0 4px 12px rgba(13,27,42,0.28)",
                "&:hover": { bgcolor: "#142b46" },
              }}
            >
              Capture
            </Button>
          </>
        )}

        {stage === "crop" && (
          <>
            <Button
              startIcon={<CachedOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={() => { setImageSrc(null); setStage("choose"); }}
              sx={{ textTransform: "none", fontWeight: 600, fontSize: 13, color: "#64748b", borderRadius: "10px", px: 2 }}
            >
              Start Over
            </Button>
            <Box flex={1} />
            <Button
              variant="contained" disableElevation
              onClick={handleConfirm}
              disabled={processing || !croppedArea}
              sx={{
                textTransform: "none", fontWeight: 700, fontSize: 13,
                bgcolor: "#1E4D8F", color: "#fff",
                borderRadius: "10px", px: 2.6, py: 1,
                boxShadow: "0 4px 12px rgba(13,27,42,0.28)",
                "&:hover": { bgcolor: "#142b46" },
                "&.Mui-disabled": { bgcolor: "#cbd5e1", color: "#fff", boxShadow: "none" },
              }}
            >
              {processing ? (
                <Stack direction="row" alignItems="center" gap={1}>
                  <CircularProgress size={14} sx={{ color: "#fff" }} />
                  Processing…
                </Stack>
              ) : "Save Photo"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AvatarPhotoEditor;

/* ═══════════════════════════════════════════════════════════════════════
   Local UI helpers
   ═══════════════════════════════════════════════════════════════════════ */

function SourceTile({
  icon, title, subtitle, onClick,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        flex: 1, cursor: "pointer", userSelect: "none",
        borderRadius: "12px",
        border: "1.5px solid #e5e7eb",
        bgcolor: "#fff",
        px: 2, py: 2.4,
        display: "flex", flexDirection: "column", alignItems: "center", gap: 0.8,
        textAlign: "center",
        transition: "all 0.18s",
        "&:hover": {
          borderColor: "#1a3353",
          boxShadow: "0 6px 18px rgba(13,27,42,0.08)",
          transform: "translateY(-1px)",
        },
      }}
    >
      <Box sx={{
        width: 52, height: 52, borderRadius: "12px",
        bgcolor: "#f1f5f9", color: "#1a3353",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {icon}
      </Box>
      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#1a3353", mt: 0.4 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 11.5, color: "#94a3b8", lineHeight: 1.4 }}>
        {subtitle}
      </Typography>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   renderCroppedBlob — given a source image URL/dataURL and the
   pixel-space crop rect react-easy-crop reports, draw to a square
   canvas at `outputSize` and return as a JPEG Blob.
   ═══════════════════════════════════════════════════════════════════════ */

async function renderCroppedBlob(src: string, area: Area, outputSize: number): Promise<Blob> {
  const img = await loadImage(src);
  const canvas = document.createElement("canvas");
  canvas.width  = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas-2d-unavailable");

  /* 1. Paint a solid WHITE base layer first.
        JPEG has no alpha channel — any transparent pixels emitted
        by drawImage (e.g. when the crop rect extends past the
        image edges at low zoom) get serialized as BLACK. Filling
        the canvas with white first guarantees a clean background. */
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, outputSize, outputSize);

  /* 2. Clamp the crop rectangle to the actual image bounds so
        drawImage never reads outside the source. Any padding the
        user introduced at low zoom is replaced by the white base. */
  const sx = Math.max(0, area.x);
  const sy = Math.max(0, area.y);
  const sw = Math.min(area.width,  img.naturalWidth  - sx);
  const sh = Math.min(area.height, img.naturalHeight - sy);
  // Translate that clamp into the output canvas — preserve the
  // operator's intended framing (left edge stays anchored).
  const scale = outputSize / area.width;
  const dx = (sx - area.x) * scale;
  const dy = (sy - area.y) * scale;
  const dw = sw * scale;
  const dh = sh * scale;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("blob-failed"))),
      "image/jpeg",
      0.92
    );
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // `crossOrigin` left UNSET — the inputs to this function are
    // always client-side blobs (FileReader dataURL or canvas dataURL
    // from the webcam capture), so CORS doesn't apply. Setting
    // crossOrigin on a data: URL is harmless but unnecessary, and
    // setting it on a same-origin URL can taint the canvas in some
    // edge cases. Leaving it off is the safe default here.
    img.onload  = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
